import React, { useMemo } from 'react';

import Table from '../common/Table';
import {
    Typography,
    Box,
    Stack,
    Chip,
    Divider,
    Collapse,
    TextField,
    Autocomplete,
    Button,
    Checkbox
} from '@mui/material';
import { CloseOutlined, UserAddOutlined } from '@ant-design/icons';
import { DEVICE_STATUSES } from 'utils/constants/statuses';
import CredentialGrantRowActions from 'sections/credential-grants/CredentialGrantRowActions';
import SponsoringOrgCell from 'sections/credential-grants/SponsoringOrgCell';
import { grantRowStatus } from 'utils/helpers/credential-grant.helper';
import useLocalTableFilters from '../../hooks/useLocalTableFilters';
import { uniqueSorted } from '../../utils/array';
import toTitleCase from '../../utils/title-case';

const EMPTY_FILTERS = {
    facilityCode: '',
    deviceNumber: '',
    deviceType: '',
    owner: '',
    accessGroup: '',
    status: ''
};

const isGrantRow = device => device.row_type === 'grant';

const groupsOf = device =>
    isGrantRow(device) && device.status === 'pending'
        ? device.proposed_access_groups
        : device.access_groups;

const DeviceTable = ({
    data,
    onDeviceClick,
    globalFilter,
    showFilters = false,
    // Load-more passthrough — forwarded straight to Table
    loadMore,
    hasMore,
    onLoadMore,
    loadingMore,
    deviceTypes = {},
    grantActions,
    selection,
    onInviteHolder
}) => {
    if (process.env.NODE_ENV !== 'production' && showFilters && loadMore) {
        // eslint-disable-next-line no-console
        console.warn(
            'DeviceTable: showFilters filters only the currently-loaded page when loadMore is set — the server-paginated rows outside that page are not searched.'
        );
    }

    const { filters, handleFilterChange, handleResetFilters, hasActiveFilters } =
        useLocalTableFilters(EMPTY_FILTERS, showFilters);

    const facilityCodeOptions = useMemo(
        () => uniqueSorted(data, 'facility_code'),
        [data]
    );
    const hasSharing = useMemo(
        () => (data || []).some(device => device.sharing || isGrantRow(device)),
        [data]
    );
    const hasGrantRows = useMemo(() => (data || []).some(isGrantRow), [data]);
    const selectableRows = useMemo(
        () =>
            (data || []).filter(
                device => isGrantRow(device) && !device.is_agreement_paused
            ),
        [data]
    );
    const deviceTypeOptions = useMemo(
        () =>
            uniqueSorted(data, 'device_type').map(type => ({
                label: deviceTypes[type] || type,
                value: type
            })),
        [data, deviceTypes]
    );
    const accessGroupOptions = useMemo(
        () => uniqueSorted((data || []).flatMap(d => d.access_groups || []), 'name'),
        [data]
    );
    const statusOptions = useMemo(
        () =>
            uniqueSorted(data, 'status').map(status => ({
                label: toTitleCase(status),
                value: status
            })),
        [data]
    );

    const filteredData = useMemo(() => {
        if (!showFilters) return data;
        const facilityCode = filters.facilityCode;
        const deviceNumber = filters.deviceNumber.trim().toLowerCase();
        const owner = filters.owner.trim().toLowerCase();
        return (data || []).filter(device => {
            if (facilityCode && device.facility_code !== facilityCode) return false;
            if (
                deviceNumber &&
                !(device.device_number || '').toLowerCase().includes(deviceNumber)
            )
                return false;
            if (filters.deviceType && device.device_type !== filters.deviceType)
                return false;
            if (owner && !(device.owner_name || '').toLowerCase().includes(owner))
                return false;
            if (
                filters.accessGroup &&
                !(device.access_groups || []).some(g => g.name === filters.accessGroup)
            )
                return false;
            if (filters.status && device.status !== filters.status) return false;
            return true;
        });
    }, [data, filters, showFilters]);

    const columns = useMemo(
        () => [
            ...(selection
                ? [
                      {
                          id: 'selection',
                          disableSortBy: true,
                          Header: () => {
                              const capped = selectableRows.slice(0, selection.limit);
                              const selectedCount = capped.filter(device =>
                                  selection.selectedIds.has(device.id)
                              ).length;
                              return (
                                  <Checkbox
                                      checked={
                                          capped.length > 0 &&
                                          selectedCount === capped.length
                                      }
                                      indeterminate={
                                          selectedCount > 0 &&
                                          selectedCount < capped.length
                                      }
                                      disabled={capped.length === 0}
                                      onChange={selection.onToggleAll}
                                  />
                              );
                          },
                          Cell: ({ row }) => {
                              const device = row.original;
                              if (!isGrantRow(device) || device.is_agreement_paused)
                                  return null;
                              const checked = selection.selectedIds.has(device.id);
                              return (
                                  <Checkbox
                                      checked={checked}
                                      disabled={
                                          !checked &&
                                          selection.selectedIds.size >= selection.limit
                                      }
                                      onClick={event => event.stopPropagation()}
                                      onChange={() => selection.onToggle(device)}
                                  />
                              );
                          }
                      }
                  ]
                : []),
            {
                Header: 'Facility Code',
                accessor: 'facility_code',
                Cell: ({ value }) => value || '—'
            },
            {
                Header: 'Device No.',
                accessor: 'device_number',
                className: 'cell-center',
                Cell: ({ value }) => value || '—'
            },
            {
                Header: 'Device Type',
                accessor: 'device_type',
                className: 'cell-center',
                Cell: ({ row, value }) =>
                    isGrantRow(row.original) ? (
                        <Stack spacing={0.5} alignItems="center">
                            <span>{deviceTypes[value] || value}</span>
                            <Chip size="small" variant="outlined" label="Shared" />
                        </Stack>
                    ) : (
                        deviceTypes[value] || value
                    )
            },
            {
                Header: 'Owner',
                accessor: 'owner_name',
                className: 'cell-center',
                Cell: ({ row, value }) => {
                    const device = row.original;
                    if (!isGrantRow(device)) return value;
                    return (
                        <Stack spacing={0.5} alignItems="center">
                            <span>{value}</span>
                            {device.holder_status === 'invited' && (
                                <Chip
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    label="Invited"
                                />
                            )}
                            {device.holder_status === 'not_invited' && onInviteHolder && (
                                <Button
                                    type="button"
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<UserAddOutlined />}
                                    sx={{
                                        height: 24,
                                        py: 0,
                                        minWidth: 'max-content',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                        fontWeight: 500
                                    }}
                                    onClick={event => {
                                        event.stopPropagation();
                                        onInviteHolder(device);
                                    }}
                                >
                                    Send invite
                                </Button>
                            )}
                        </Stack>
                    );
                }
            },
            {
                Header: 'Access Groups',
                accessor: 'access_groups',
                className: 'cell-right',
                Cell: ({ row }) => {
                    const groups = groupsOf(row.original);
                    return groups && groups.length
                        ? groups.map(g => g.name).join(', ')
                        : '—';
                }
            },
            // Only where at least one row is consumer-issued (SD-3287) or shared — every other device table in
            // the app (property / organization panels) renders through here too and must not grow
            // a column of dashes.
            ...(hasSharing
                ? [
                      {
                          Header: 'Sponsoring Org',
                          accessor: 'sharing_org_name',
                          className: 'cell-center',
                          Cell: ({ row }) => {
                              const device = row.original;
                              if (isGrantRow(device)) {
                                  return (
                                      <SponsoringOrgCell
                                          value={device.sharing_org_name}
                                          row={row}
                                      />
                                  );
                              }
                              if (!device.sharing) return '—';
                              return (
                                  <Stack spacing={0.5} alignItems="center">
                                      <Typography variant="body2">
                                          {device.sharing_org_name}
                                      </Typography>
                                      {device.sharing_chip && (
                                          <Chip
                                              size="small"
                                              color={device.sharing_chip.color}
                                              label={device.sharing_chip.label}
                                          />
                                      )}
                                  </Stack>
                              );
                          }
                      }
                  ]
                : []),
            {
                Header: 'Status',
                accessor: 'status',
                className: 'cell-center',
                Cell: ({ row, value }) => {
                    const grant = isGrantRow(row.original);
                    const label = grant ? grantRowStatus(value) : value;
                    const statusConfig = DEVICE_STATUSES[label];
                    const badge = (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '80%',
                                backgroundColor: statusConfig?.background,
                                borderRadius: '5px',
                                border: `1px solid ${statusConfig?.border}`,
                                color: statusConfig?.color
                            }}
                        >
                            <Typography
                                sx={{ fontSize: '13px', marginX: '10px' }}
                                component="span"
                                variant="body1"
                                textTransform="capitalize"
                            >
                                {label}
                            </Typography>
                        </div>
                    );
                    if (!(grant && row.original.is_agreement_paused)) return badge;
                    return (
                        <Stack spacing={0.5} alignItems="center">
                            {badge}
                            <Chip
                                size="small"
                                color="warning"
                                variant="outlined"
                                label="Agreement paused"
                            />
                        </Stack>
                    );
                }
            },
            ...(grantActions && hasGrantRows
                ? [
                      {
                          Header: '',
                          id: 'grant-actions',
                          disableSortBy: true,
                          Cell: ({ row }) =>
                              isGrantRow(row.original) ? (
                                  <CredentialGrantRowActions
                                      grant={row.original}
                                      {...grantActions}
                                  />
                              ) : null
                      }
                  ]
                : [])
        ],
        [
            deviceTypes,
            hasSharing,
            hasGrantRows,
            grantActions,
            selection,
            selectableRows,
            onInviteHolder
        ]
    );

    return (
        <>
            <Collapse in={showFilters}>
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'flex-start' }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(200px, 1fr))',
                                gap: 1.5
                            }}
                        >
                            <Autocomplete
                                options={facilityCodeOptions}
                                value={filters.facilityCode || null}
                                onChange={(_, value) =>
                                    handleFilterChange('facilityCode', value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Facility Code" />
                                )}
                            />
                            <TextField
                                label="Device No."
                                size="small"
                                fullWidth
                                value={filters.deviceNumber}
                                onChange={e =>
                                    handleFilterChange('deviceNumber', e.target.value)
                                }
                            />
                            <Autocomplete
                                options={deviceTypeOptions}
                                getOptionLabel={option => option.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                value={
                                    deviceTypeOptions.find(
                                        o => o.value === filters.deviceType
                                    ) || null
                                }
                                onChange={(_, value) =>
                                    handleFilterChange('deviceType', value?.value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Device Type" />
                                )}
                            />
                            <TextField
                                label="Owner"
                                size="small"
                                fullWidth
                                value={filters.owner}
                                onChange={e =>
                                    handleFilterChange('owner', e.target.value)
                                }
                            />
                            <Autocomplete
                                options={accessGroupOptions}
                                value={filters.accessGroup || null}
                                onChange={(_, value) =>
                                    handleFilterChange('accessGroup', value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Access Group" />
                                )}
                            />
                            <Autocomplete
                                options={statusOptions}
                                getOptionLabel={option => option.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                value={
                                    statusOptions.find(
                                        o => o.value === filters.status
                                    ) || null
                                }
                                onChange={(_, value) =>
                                    handleFilterChange('status', value?.value || '')
                                }
                                size="small"
                                renderInput={params => (
                                    <TextField {...params} label="Status" />
                                )}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloseOutlined />}
                            onClick={handleResetFilters}
                            disabled={!hasActiveFilters}
                            sx={{ flexShrink: 0 }}
                        >
                            Reset
                        </Button>
                    </Stack>
                </Box>
                <Divider />
            </Collapse>
            <Table
                columns={columns}
                data={filteredData}
                onRowClick={onDeviceClick}
                externalGlobalFilter={globalFilter}
                pagination={!loadMore}
                loadMore={loadMore}
                hasMore={hasMore}
                onLoadMore={onLoadMore}
                loadingMore={loadingMore}
            />
        </>
    );
};

export default DeviceTable;
